import React from 'react';
import type { CSSProperties } from 'react';
import { mergeProps } from 'react-aria';
import type { ListBoxItemRenderProps } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgSplit } from '@actual-app/components/icons/v0';
import { SvgTime } from '@actual-app/components/icons/v1';
import {
  SvgAlertTriangle,
  SvgCalendar3,
  SvgCheckCircle1,
  SvgLockClosed,
} from '@actual-app/components/icons/v2';
import { Text } from '@actual-app/components/text';
import { TextOneLine } from '@actual-app/components/text-one-line';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { isPreviewId } from '@actual-app/core/shared/transactions';
import { titleFirst } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type {
  AccountEntity,
  TransactionEntity,
} from '@actual-app/core/types/models';
import {
  PressResponder,
  useLongPress,
  usePress,
} from '@react-aria/interactions';

import { TransferDirectionIcon } from '#components/common/TransferDirectionIcon';
import { useColoresCategorias } from '#components/mobile/informes/useColoresCategorias';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { ImporteMovimiento } from '#components/mobile/ui/ImporteMovimiento';
import { Pildora } from '#components/mobile/ui/Pildora';
import type { EstadoPildora } from '#components/mobile/ui/Pildora';
import { PildoraCategoria } from '#components/mobile/ui/PildoraCategoria';
import { color, colorCategoria, num } from '#components/mobile/ui/tokens';
import { InicialAutor } from '#components/pareja/InicialAutor';
import { autorDe } from '#components/pareja/tripulacionLogica';
import { useTripulacion } from '#components/pareja/useTripulacion';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useAccount } from '#hooks/useAccount';
import { useCategories } from '#hooks/useCategories';
import { useDisplayPayee } from '#hooks/useDisplayPayee';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { usePayee } from '#hooks/usePayee';
import { parseNotes } from '#notes/linkParser';
import { useSelector } from '#redux';
import { getStatusLabel } from '#util/schedule';

import { estadoPrevisto } from './listaMovimientos';
import type { EstadoPrevisto } from './listaMovimientos';
import { lookupName } from './TransactionEdit';

export const ROW_HEIGHT = 64;

/**
 * Separa las #etiquetas de la nota (H62): van en la línea de la categoría y
 * el resto de la nota, en texto plano debajo.
 */
export function separarEtiquetas(notas: string | null | undefined): {
  etiquetas: string[];
  notasSinEtiquetas: string;
} {
  if (!notas) {
    return { etiquetas: [], notasSinEtiquetas: '' };
  }
  const etiquetas: string[] = [];
  let resto = '';
  for (const segmento of parseNotes(notas)) {
    if (segmento.type === 'tag') {
      etiquetas.push(segmento.content);
    } else {
      resto += segmento.content;
    }
  }
  return { etiquetas, notasSinEtiquetas: resto.replace(/\s+/g, ' ').trim() };
}

// H60: los pagos fijos ya no van en cursiva; se distinguen por el fondo y
// por la píldora de estado.
const getTextStyle = (): CSSProperties => ({
  fontSize: 14,
  color: color.fg,
});

type TransactionListItemProps = ListBoxItemRenderProps & {
  transaction?: TransactionEntity;
  showRunningBalance?: boolean;
  runningBalance?: IntegerAmount;
  isReconciling?: boolean;
  onPress: (transaction: TransactionEntity) => void;
  onLongPress: (transaction: TransactionEntity) => void;
  onToggleCleared?: (transaction: TransactionEntity) => void;
};

export function TransactionListItem({
  showRunningBalance,
  runningBalance,
  isReconciling = false,
  onPress,
  onLongPress,
  onToggleCleared,
  transaction,
  ...itemProps
}: TransactionListItemProps) {
  const { t } = useTranslation();
  const { data: { list: categories } = { list: [] } } = useCategories();
  const format = useFormat();
  const { huecoDe } = useColoresCategorias();
  const tripulantes = useTripulacion();

  const { data: payee } = usePayee(transaction?.payee);
  const displayPayee = useDisplayPayee({ transaction });

  const account = useAccount(transaction?.account || '');
  const transferAccount = useAccount(payee?.transfer_acct || '');
  const isPreview = isPreviewId(transaction?.id || '');

  const newTransactions = useSelector(
    state => state.transactions.newTransactions,
  );

  const { longPressProps } = useLongPress({
    accessibilityDescription: 'Long press to select multiple transactions',
    onLongPress: () => {
      if (isPreview) {
        return;
      }

      onLongPress(transaction!);
    },
  });

  const { pressProps } = usePress({
    onPress: () => {
      onPress(transaction!);
    },
  });

  if (!transaction) {
    return null;
  }

  const {
    id,
    amount,
    category: categoryId,
    cleared: isCleared,
    reconciled: isReconciled,
    is_parent: isParent,
    is_child: isChild,
    notes,
    forceUpcoming,
  } = transaction;

  const displayedNotes = notes;
  const { etiquetas, notasSinEtiquetas: notasSueltas } =
    separarEtiquetas(displayedNotes);
  // En los previstos no hay línea de categoría: las etiquetas van en la nota.
  const notasSinEtiquetas = isPreview
    ? [notasSueltas, ...etiquetas].filter(Boolean).join(' ')
    : notasSueltas;

  const previewStatus = estadoPrevisto(categoryId, forceUpcoming);

  const isAdded = newTransactions.includes(id);
  const categoryName = lookupName(categories, categoryId);
  const specialCategory = account?.offbudget
    ? t('Off budget')
    : transferAccount && !transferAccount.offbudget
      ? t('Transfer (type)')
      : isParent
        ? t('Split (several categories)')
        : null;

  const prettyCategory = specialCategory || categoryName;
  const textStyle = getTextStyle();

  const { emoji } = categoryName ? separarEmoji(categoryName) : { emoji: null };
  const hueco = categoryId && !specialCategory ? huecoDe(categoryId) : null;
  const colorIcono = hueco != null ? colorCategoria(hueco) : undefined;
  // Pareja: inicial de quien lo apuntó, sobre el icono.
  const autor = autorDe(tripulantes, transaction.autor);

  return (
    <View
      // Ayuda con Gavi: qué tipo de fila es (la que enseña cada paso).
      data-ayuda-fila={
        isPreview ? 'previsto' : prettyCategory ? 'normal' : 'sin-categoria'
      }
      data-ayuda-nuevo={transaction.reviewed === false || undefined}
      style={{
        flexDirection: 'row',
        alignItems: 'stretch',
        width: '100%',
        minHeight: ROW_HEIGHT,
        overflow: 'hidden',
        borderTop: `1px solid ${color.line}`,
        backgroundColor: itemProps.isSelected
          ? color.accentSoft
          : isPreview
            ? color.surface2
            : color.surface,
        ...(itemProps.isSelected && {
          boxShadow: `inset 4px 0 0 ${color.accent}`,
        }),
      }}
    >
      <PressResponder {...mergeProps(pressProps, longPressProps)}>
        <Button
          {...itemProps}
          style={{
            userSelect: 'none',
            minHeight: ROW_HEIGHT,
            flex: 1,
            borderRadius: 0,
            borderWidth: 0,
            padding: '8px 14px',
            backgroundColor: 'transparent',
            ...(isReconciling && { paddingRight: 0 }),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              minWidth: 0,
            }}
          >
            <View style={{ position: 'relative', flexShrink: 0 }}>
              <IconoCaja
                size={36}
                icono={
                  emoji
                    ? undefined
                    : transferAccount
                      ? 'move'
                      : isParent
                        ? 'arrowupdown'
                        : 'tag'
                }
                colorPropio={colorIcono}
                style={{ fontSize: 16 }}
              >
                {emoji}
              </IconoCaja>
              {autor && (
                <View style={{ position: 'absolute', right: -4, bottom: -4 }}>
                  <InicialAutor tripulante={autor} size={17} />
                </View>
              )}
            </View>
            <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
              {/* H66: el beneficiario en una línea, con «…» si no cabe. */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                <PayeeIcons
                  transaction={transaction}
                  transferAccount={transferAccount}
                />
                {transaction.reviewed === false && (
                  // «Nuevo»: llegó del banco o de un archivo y está por
                  // revisar (cuenta igual en saldos y presupuesto).
                  <span
                    data-testid="punto-nuevo"
                    title={t('New')}
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      flexShrink: 0,
                      marginRight: 5,
                      backgroundColor: color.accent,
                    }}
                  />
                )}
                <TextOneLine
                  data-ayuda="comercio"
                  style={{
                    ...textStyle,
                    minWidth: 0,
                    flexShrink: 1,
                    fontWeight: 800,
                    ...(isAdded && { color: color.accent }),
                    ...(!displayPayee && !isPreview
                      ? {
                          color: color.fg3,
                          fontStyle: 'italic',
                        }
                      : {}),
                  }}
                >
                  {displayPayee || t('(No payee)')}
                </TextOneLine>
              </View>
              {isPreview ? (
                <PildoraPrevisto
                  estado={previewStatus}
                  fecha={transaction.date}
                  isSplit={isParent || isChild}
                />
              ) : (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                    minWidth: 0,
                  }}
                >
                  {!isReconciling &&
                    (isReconciled ? (
                      <SvgLockClosed
                        style={{
                          width: 11,
                          height: 11,
                          flexShrink: 0,
                          color: color.ok,
                        }}
                      />
                    ) : (
                      <SvgCheckCircle1
                        data-ayuda="confirmado"
                        style={{
                          width: 11,
                          height: 11,
                          flexShrink: 0,
                          color: isCleared ? color.ok : color.fg3,
                        }}
                      />
                    ))}
                  {(isParent || isChild) && (
                    <SvgSplit
                      style={{ width: 12, height: 12, flexShrink: 0 }}
                    />
                  )}
                  {prettyCategory && !specialCategory ? (
                    <PildoraCategoria
                      data-ayuda="categoria"
                      nombre={prettyCategory}
                      hueco={hueco}
                      tamano="pequena"
                    />
                  ) : (
                    <TextOneLine
                      data-ayuda="categoria"
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: prettyCategory ? color.fg3 : color.warn,
                        fontStyle: 'italic',
                        textAlign: 'left',
                      }}
                    >
                      {prettyCategory || t('Uncategorized')}
                    </TextOneLine>
                  )}
                  {etiquetas.length > 0 && (
                    // H62: las #etiquetas, en texto pequeño junto a la
                    // categoría (sin las píldoras de colores de Actual).
                    <TextOneLine
                      data-testid="etiquetas-movimiento"
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: color.fg3,
                        textAlign: 'left',
                        minWidth: 0,
                      }}
                    >
                      {etiquetas.join(' ')}
                    </TextOneLine>
                  )}
                </View>
              )}
              {notasSinEtiquetas && (
                <TextOneLine
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: color.fg3,
                    textAlign: 'left',
                  }}
                >
                  {notasSinEtiquetas}
                </TextOneLine>
              )}
            </View>
            <View
              data-ayuda="importe"
              style={{
                justifyContent: 'center',
                alignItems: 'flex-end',
                flexShrink: 0,
              }}
            >
              <ImporteMovimiento valor={amount} />
              {showRunningBalance && runningBalance !== undefined && (
                <PrivacyFilter>
                  <Text
                    style={{
                      ...num,
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: runningBalance < 0 ? color.bad : color.fg3,
                    }}
                  >
                    {format(runningBalance, 'financial')}
                  </Text>
                </PrivacyFilter>
              )}
            </View>
          </View>
        </Button>
      </PressResponder>
      {isReconciling &&
        !isPreview &&
        (isChild ? (
          <View
            style={{
              width: 32,
              flexShrink: 0,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ClearedStatusIcon
              isReconciled={isReconciled}
              isCleared={isCleared}
            />
          </View>
        ) : (
          <Button
            variant="bare"
            aria-label={
              isReconciled
                ? t('Unlock reconciled transaction')
                : isCleared
                  ? t('Unclear transaction')
                  : t('Clear transaction')
            }
            style={{
              width: 32,
              height: '100%',
              flexShrink: 0,
              borderRadius: 0,
            }}
            onPress={() => onToggleCleared?.(transaction)}
          >
            <ClearedStatusIcon
              isReconciled={isReconciled}
              isCleared={isCleared}
            />
          </Button>
        ))}
    </View>
  );
}

const PILDORA_PREVISTO: Record<
  EstadoPrevisto,
  { estado: EstadoPildora; Icono: typeof SvgCalendar3 }
> = {
  missed: { estado: 'rojo', Icono: SvgTime },
  due: { estado: 'aviso', Icono: SvgAlertTriangle },
  'on-way': { estado: 'aviso', Icono: SvgTime },
  upcoming: { estado: 'neutro', Icono: SvgCalendar3 },
};

type PildoraPrevistoProps = {
  estado: EstadoPrevisto | null;
  fecha: string;
  isSplit?: boolean;
};

/**
 * Estado de un pago fijo en la lista (H60): «Atrasado · 3 oct» en rojo con
 * reloj, «En camino · 5 oct» (margen tras el cargo) y «Toca hoy» en ámbar y
 * «Próximo · 5 oct» en gris con calendario.
 */
export function PildoraPrevisto({
  estado,
  fecha,
  isSplit = false,
}: PildoraPrevistoProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (!estado) {
    return null;
  }
  const { estado: tono, Icono } = PILDORA_PREVISTO[estado];
  // Pagos fijos (domiciliados): «Atrasado» y «En camino» son propios;
  // «Toca hoy» y «Próximo», los de siempre.
  const etiqueta =
    estado === 'missed'
      ? t('Late')
      : estado === 'on-way'
        ? t('On its way')
        : titleFirst(getStatusLabel(estado));
  const conFecha =
    estado === 'due'
      ? etiqueta
      : `${etiqueta} · ${monthUtils.format(fecha, 'd MMM', locale)}`;
  return (
    <View style={{ flexDirection: 'row' }}>
      <Pildora
        estado={tono}
        data-testid="pildora-previsto"
        style={{ minHeight: 20, padding: '0 8px', fontSize: 11.5 }}
      >
        <Icono style={{ width: 11, height: 11, flexShrink: 0 }} />
        {isSplit ? t('{{status}} (Split)', { status: conFecha }) : conFecha}
      </Pildora>
    </View>
  );
}

type ClearedStatusIconProps = {
  isReconciled?: boolean;
  isCleared?: boolean;
};

function ClearedStatusIcon({
  isReconciled,
  isCleared,
}: ClearedStatusIconProps) {
  return isReconciled ? (
    <SvgLockClosed
      style={{
        width: 13,
        height: 13,
        color: theme.noticeTextLight,
      }}
    />
  ) : (
    <SvgCheckCircle1
      style={{
        width: 13,
        height: 13,
        color: isCleared ? theme.noticeTextLight : theme.pageTextSubdued,
      }}
    />
  );
}

type PayeeIconsProps = {
  transaction: TransactionEntity;
  transferAccount?: AccountEntity;
};

function PayeeIcons({ transaction, transferAccount }: PayeeIconsProps) {
  // Sin schedules en Rumbo: solo el icono de traspaso.
  if (!transferAccount) {
    return null;
  }
  return (
    <TransferDirectionIcon
      isDeposit={transaction.amount > 0}
      style={{ width: 12, height: 12, marginRight: 5 }}
    />
  );
}
